import {
  Stack,
  Card,
  Text,
  TextInput,
  Select,
  Group,
  Button,
  Textarea,
  Stepper,
  Divider,
  Alert,
  ActionIcon,
  Title,
  Badge,
  Loader,
  Center,
  Grid,
} from '@mantine/core';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconArrowLeft,
  IconArrowRight,
  IconBuilding,
  IconCalendar,
  IconCheck,
  IconCircleCheck,
  IconRoute,
  IconTruck,
  IconUser,
  IconX,
} from '@tabler/icons-react';
import {
  useCreateEnvio,
  useEmpresas,
  useEnvio,
  useUpdateEnvio,
  useVehiculos,
  useConductores,
  getErrorMessage,
} from '../../api/hooks';
import type { Envio } from '../../api/types';

/**
 * Wizard de alta o edición de un envío (cabecera de viaje).
 *
 * Sprint X (refactor): el envío ya NO incluye origen/destino/detalleMercancía —
 * esos campos viven en el recurso Servicio (1 DeCA → N Servicios).
 *
 * Modos:
 *   - Sin `id` en la URL → CREACIÓN (POST /envios). El envío queda en BORRADOR.
 *   - Con `id` en la URL → EDICIÓN (PATCH /envios/:id). Solo permitido en
 *     estado BORRADOR (el resto se gestionan vía DeCA, ver DeCADetail).
 *
 * Tipo de servicio: fijo en MERCANCIA por ahora. El modelo soporta los
 * 4 tipos legales pero la UI la creamos cuando los necesitemos (no es MVP).
 *
 * Flujo (3 pasos):
 *  1. Partes (cargador, transportista, vehículo, conductor).
 *  2. Trayecto (fecha, hora salida, fecha fin opcional, observaciones).
 *  3. Confirmar (resumen + submit).
 *
 * Después del submit, el envío queda en BORRADOR y el operador deberá
 * emitir el DeCA desde la lista (con modal de servicios).
 */

interface FormValues {
  cargadorId: string;
  transportistaId: string;
  vehiculoId: string;
  conductorId: string;
  fechaServicio: string;
  fechaFinServicio: string;
  horaSalidaPrevista: string;
  observaciones: string;
}

const EMPTY: FormValues = {
  cargadorId: '',
  transportistaId: '',
  vehiculoId: '',
  conductorId: '',
  fechaServicio: '',
  fechaFinServicio: '',
  horaSalidaPrevista: '',
  observaciones: '',
};

const STEP_LABELS = ['Partes', 'Trayecto', 'Confirmar'];

const TIPO_SERVICIO_FIJO = 'MERCANCIA' as const;

function envioToFormValues(envio: Envio): FormValues {
  return {
    cargadorId: envio.cargadorId,
    transportistaId: envio.transportistaId,
    vehiculoId: envio.vehiculoId,
    conductorId: envio.conductorId,
    fechaServicio: envio.fechaServicio,
    fechaFinServicio: envio.fechaFinServicio ?? '',
    horaSalidaPrevista: envio.horaSalidaPrevista ?? '',
    observaciones: envio.observaciones ?? '',
  };
}

export function EnvioForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEditing = !!id;

  const { data: empresas = [], error: empresasError } = useEmpresas();
  const { data: vehiculos = [], error: vehiculosError } = useVehiculos();
  const { data: conductores = [], error: conductoresError } = useConductores();
  const { data: envioExistente, isLoading: isLoadingEnvio } = useEnvio(id);
  const crear = useCreateEnvio();
  const actualizar = useUpdateEnvio();

  const initialValues: FormValues = envioExistente ? envioToFormValues(envioExistente) : EMPTY;

  const form = useForm<FormValues>({
    initialValues,
    validate: (values) => {
      const errs: Record<string, string> = {};
      if (step === 0) {
        if (!values.cargadorId) errs.cargadorId = 'Requerido';
        if (!values.transportistaId) errs.transportistaId = 'Requerido';
        if (!values.vehiculoId) errs.vehiculoId = 'Requerido';
        if (!values.conductorId) errs.conductorId = 'Requerido';
      }
      if (step === 1) {
        if (!values.fechaServicio) errs.fechaServicio = 'Requerido';
      }
      return errs;
    },
  });

  const [step, setStep] = useState(0);

  if (isEditing && isLoadingEnvio) {
    return (
      <Center mih={200}>
        <Loader />
      </Center>
    );
  }

  if (isEditing && !envioExistente) {
    return (
      <Stack gap="md" maw={1100}>
        <Group px="md" pt="md">
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={18} />}
            onClick={() => navigate('/envios')}
          >
            Volver
          </Button>
        </Group>
        <Alert
          icon={<IconAlertCircle size={20} />}
          color="red"
          variant="light"
          title="Envío no encontrado"
        >
          No hemos podido recuperar este envío.
        </Alert>
      </Stack>
    );
  }

  if (isEditing && envioExistente && envioExistente.estado !== 'BORRADOR') {
    // El backend igual validará la transición, pero mostramos un aviso
    // inmediato para no malgastar inputs del usuario.
    return (
      <Stack gap="md" maw={1100}>
        <Group px="md" pt="md">
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={18} />}
            onClick={() => navigate(`/envios/${id}`)}
          >
            Volver al envío
          </Button>
        </Group>
        <Alert
          icon={<IconAlertCircle size={20} />}
          color="orange"
          variant="light"
          title="Solo se editan envíos en BORRADOR"
        >
          Este envío está en estado {envioExistente.estado}. Los cambios una vez emitido se
          gestionan desde el DeCA (Modificar DeCA / Cancelar / Iniciar / Finalizar).
        </Alert>
      </Stack>
    );
  }

  const cargadores = empresas.filter((e) => e.tipo === 'CARGADOR' || e.tipo === 'AMBOS');
  const transportistas = empresas.filter((e) => e.tipo === 'TRANSPORTISTA' || e.tipo === 'AMBOS');

  const cargadoresData = cargadores.map((e) => ({
    value: e.id,
    label: e.razonSocial + (e.nifCif ? ` (${e.nifCif})` : ''),
  }));
  const transportistasData = transportistas.map((e) => ({
    value: e.id,
    label: e.razonSocial + (e.nifCif ? ` (${e.nifCif})` : ''),
  }));
  const vehiculosData = vehiculos
    .filter((v) => v.activo !== false)
    .map((v) => ({
      value: v.id,
      label: v.matriculaRemolque
        ? `${v.matricula} / ${v.matriculaRemolque}${v.marca ? ` — ${v.marca} ${v.modelo ?? ''}` : ''}`
        : `${v.matricula}${v.marca ? ` — ${v.marca} ${v.modelo ?? ''}` : ''}`,
    }));
  const conductoresData = conductores
    .filter((c) => c.activo !== false)
    .map((c) => ({
      value: c.id,
      label: c.dni ? `${c.nombre} (${c.dni})` : c.nombre,
    }));

  const hasCatalogError = empresasError || vehiculosError || conductoresError;

  const canNextFrom0 =
    !!form.values.cargadorId &&
    !!form.values.transportistaId &&
    !!form.values.vehiculoId &&
    !!form.values.conductorId;
  const canNextFrom1 = !!form.values.fechaServicio;
  const canSubmit = canNextFrom0 && canNextFrom1 && !crear.isPending && !actualizar.isPending;

  const handleNext = () => {
    const errs = form.validate();
    if (!errs.hasErrors) setStep((s) => s + 1);
  };

  const handleSubmit = (values: FormValues) => {
    const base = {
      tipoServicio: TIPO_SERVICIO_FIJO,
      cargadorId: values.cargadorId,
      transportistaId: values.transportistaId,
      vehiculoId: values.vehiculoId,
      conductorId: values.conductorId,
      fechaServicio: values.fechaServicio,
      horaSalidaPrevista: values.horaSalidaPrevista || undefined,
      fechaFinServicio: values.fechaFinServicio || undefined,
      observaciones: values.observaciones || undefined,
    };

    if (isEditing && envioExistente) {
      actualizar.mutate(
        { id: envioExistente.id, data: base },
        {
          onSuccess: () => {
            notifications.show({
              title: 'Envío actualizado',
              message: 'Los cambios se han guardado.',
              color: 'green',
              icon: <IconCheck size={18} />,
            });
            navigate(`/envios/${envioExistente.id}`);
          },
          onError: (e: unknown) =>
            notifications.show({
              title: 'Error al actualizar el envío',
              message: getErrorMessage(e),
              color: 'red',
              icon: <IconAlertCircle size={18} />,
            }),
        },
      );
    } else {
      crear.mutate(base, {
        onSuccess: () => {
          notifications.show({
            title: 'Envío creado',
            message: 'Ahora puedes emitir el DeCA desde la lista de envíos.',
            color: 'green',
            icon: <IconCheck size={18} />,
          });
          navigate('/envios');
        },
        onError: (e: unknown) =>
          notifications.show({
            title: 'Error al crear el envío',
            message: getErrorMessage(e),
            color: 'red',
            icon: <IconAlertCircle size={18} />,
          }),
      });
    }
  };

  const labelEmpresa = (idValue: string): string | undefined =>
    empresas.find((e) => e.id === idValue)?.razonSocial;
  const labelVehiculo = (idValue: string): string | undefined => {
    const v = vehiculos.find((x) => x.id === idValue);
    if (!v) return undefined;
    return v.matriculaRemolque ? `${v.matricula} / ${v.matriculaRemolque}` : v.matricula;
  };
  const labelConductor = (idValue: string): string | undefined =>
    conductores.find((c) => c.id === idValue)?.nombre;

  return (
    <Stack gap="md" maw={1100} mx="auto">
      {/* Cabecera */}
      <Group justify="space-between" align="center" px="md">
        <Group gap="sm">
          <ActionIcon
            variant="subtle"
            onClick={() => navigate(isEditing && id ? `/envios/${id}` : '/envios')}
            aria-label="Volver"
          >
            <IconArrowLeft size={20} />
          </ActionIcon>
          <Title order={2} fw={600}>
            {isEditing ? 'Editar envío' : 'Nuevo envío'}
          </Title>
          {isEditing && envioExistente && (
            <Badge variant="light" color="gray" ff="monospace">
              {envioExistente.id.slice(0, 8)}
            </Badge>
          )}
        </Group>
        <Button
          variant="subtle"
          color="red"
          leftSection={<IconX size={16} />}
          onClick={() => navigate(isEditing && id ? `/envios/${id}` : '/envios')}
        >
          Cancelar
        </Button>
      </Group>

      {hasCatalogError && (
        <Alert
          icon={<IconAlertCircle size={20} />}
          color="red"
          variant="light"
          title="No se pudieron cargar los catálogos"
          mx="md"
        >
          Verifica tu conexión y que tengas al menos una empresa, vehículo y conductor dados de
          alta.
        </Alert>
      )}

      <Card withBorder mx="md">
        <Stepper active={step} onStepClick={setStep} allowNextStepsSelect={false} color="deca">
          {STEP_LABELS.map((label) => (
            <Stepper.Step key={label} label={label} />
          ))}
        </Stepper>
      </Card>

      {/* Paso 0 — Partes */}
      {step === 0 && (
        <Card withBorder mx="md">
          <Stack gap="md">
            <Group gap="sm">
              <IconBuilding size={20} color="var(--mantine-color-deca-7)" />
              <Title order={4}>Partes del servicio</Title>
            </Group>
            <Text size="sm" c="dimmed">
              Selecciona las entidades que intervienen en el transporte. Cargador contractual y
              transportista efectivo son obligatorios.
            </Text>
            <Grid>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Select
                  label="Cargador contractual"
                  placeholder="Selecciona empresa cargadora"
                  data={cargadoresData}
                  searchable
                  nothingFoundMessage="No hay cargadores. Crea uno primero."
                  disabled={cargadores.length === 0}
                  required
                  {...form.getInputProps('cargadorId')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Select
                  label="Transportista efectivo"
                  placeholder="Selecciona empresa transportista"
                  data={transportistasData}
                  searchable
                  nothingFoundMessage="No hay transportistas. Crea uno primero."
                  disabled={transportistas.length === 0}
                  required
                  {...form.getInputProps('transportistaId')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Select
                  label="Vehículo"
                  placeholder="Selecciona vehículo"
                  data={vehiculosData}
                  searchable
                  nothingFoundMessage="No hay vehículos. Crea uno primero."
                  disabled={vehiculos.length === 0}
                  required
                  leftSection={<IconTruck size={14} />}
                  {...form.getInputProps('vehiculoId')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Select
                  label="Conductor"
                  placeholder="Selecciona conductor"
                  data={conductoresData}
                  searchable
                  nothingFoundMessage="No hay conductores. Crea uno primero."
                  disabled={conductores.length === 0}
                  required
                  leftSection={<IconUser size={14} />}
                  {...form.getInputProps('conductorId')}
                />
              </Grid.Col>
            </Grid>
            <Text size="xs" c="dimmed">
              Si no tienes lo que necesitas, ve a <b>Vehículos</b> o <b>Conductores</b> para darlo
              de alta y vuelve.
            </Text>
          </Stack>
        </Card>
      )}

      {/* Paso 1 — Trayecto */}
      {step === 1 && (
        <Card withBorder mx="md">
          <Stack gap="md">
            <Group gap="sm">
              <IconCalendar size={20} color="var(--mantine-color-deca-7)" />
              <Title order={4}>Fechas y trayecto</Title>
            </Group>
            <Text size="sm" c="dimmed">
              Fecha y hora previstas del servicio. El origen y el destino se definirán al emitir el
              DeCA (recurso Servicio).
            </Text>
            <Grid>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <TextInput
                  label="Fecha de servicio"
                  type="date"
                  required
                  {...form.getInputProps('fechaServicio')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <TextInput
                  label="Hora salida prevista"
                  type="time"
                  {...form.getInputProps('horaSalidaPrevista')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <TextInput
                  label="Fecha fin servicio (opcional)"
                  type="date"
                  description="Solo si el servicio dura más de un día"
                  {...form.getInputProps('fechaFinServicio')}
                />
              </Grid.Col>
            </Grid>
            <Textarea
              label="Observaciones"
              autosize
              minRows={2}
              {...form.getInputProps('observaciones')}
            />
          </Stack>
        </Card>
      )}

      {/* Paso 2 — Confirmar */}
      {step === 2 && (
        <Card withBorder mx="md">
          <Stack gap="md">
            <Group gap="sm">
              <IconCircleCheck size={20} color="var(--mantine-color-deca-7)" />
              <Title order={4}>Resumen</Title>
            </Group>
            <Divider />
            <SummaryRow
              icon={IconBuilding}
              label="Cargador contractual"
              value={labelEmpresa(form.values.cargadorId)}
            />
            <SummaryRow
              icon={IconTruck}
              label="Transportista efectivo"
              value={labelEmpresa(form.values.transportistaId)}
            />
            <SummaryRow
              icon={IconTruck}
              label="Vehículo"
              value={labelVehiculo(form.values.vehiculoId)}
            />
            <SummaryRow
              icon={IconUser}
              label="Conductor"
              value={labelConductor(form.values.conductorId)}
            />
            <Divider />
            <SummaryRow
              icon={IconCalendar}
              label="Fecha de servicio"
              value={form.values.fechaServicio}
            />
            <SummaryRow
              icon={IconRoute}
              label="Hora salida prevista"
              value={form.values.horaSalidaPrevista || '—'}
            />
            <SummaryRow
              icon={IconCalendar}
              label="Fecha fin servicio"
              value={form.values.fechaFinServicio || '—'}
            />
            {form.values.observaciones.trim() && (
              <SummaryRow
                icon={IconRoute}
                label="Observaciones"
                value={form.values.observaciones}
              />
            )}
            <Divider />
            <Alert color="deca" variant="light" icon={<IconCheck size={16} />}>
              {isEditing
                ? 'Al pulsar "Guardar cambios" se actualizará la cabecera del envío (sigue en BORRADOR).'
                : 'Al pulsar "Crear envío" la cabecera quedará en BORRADOR. Después emite el DeCA desde la lista de envíos.'}
            </Alert>
          </Stack>
        </Card>
      )}

      {/* Botones de navegación */}
      <Group justify="space-between" px="md" pb="md">
        <Button
          variant="default"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          disabled={step === 0}
        >
          Atrás
        </Button>
        {step < STEP_LABELS.length - 1 ? (
          <Button
            rightSection={<IconArrowRight size={16} />}
            onClick={handleNext}
            disabled={(step === 0 && !canNextFrom0) || (step === 1 && !canNextFrom1)}
          >
            Siguiente
          </Button>
        ) : (
          <Button
            leftSection={<IconCheck size={16} />}
            color="deca"
            onClick={() => handleSubmit(form.values)}
            disabled={!canSubmit}
            loading={crear.isPending || actualizar.isPending}
          >
            {isEditing ? 'Guardar cambios' : 'Crear envío'}
          </Button>
        )}
      </Group>
    </Stack>
  );
}

// ────────────────────────────────────────────────────────────────────
// Subcomponentes de layout uniforme (mismo patron que DeCAWizard)
// ────────────────────────────────────────────────────────────────────

interface SummaryRowProps {
  icon: typeof IconRoute;
  label: string;
  value: string | undefined;
}
function SummaryRow({ icon: IconCmp, label, value }: SummaryRowProps) {
  return (
    <Group justify="space-between" align="center" wrap="nowrap" gap="md">
      <Group gap={6} wrap="nowrap" style={{ minWidth: 0, flex: '0 0 auto' }}>
        <IconCmp size={14} color="var(--mantine-color-gray-6)" />
        <Text size="sm" c="dimmed">
          {label}
        </Text>
      </Group>
      <Text size="sm" ta="right" style={{ minWidth: 0, flex: 1 }}>
        {value || '—'}
      </Text>
    </Group>
  );
}
